import { BarChart3, BookUser, CalendarCheck, FileCheck2, FileText, LayoutDashboard, Scale, Settings, ShoppingCart } from 'lucide-react'

/** Mirrors backend/internal/util/roles.go and the users_role_check constraint. */
export const ROLES = ['admin', 'counter'] as const
export type Role = (typeof ROLES)[number]

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value)
}

export interface NavItem {
  id: string
  label: string
  to: string
  icon: typeof ShoppingCart
  /** Roles that may open it. */
  roles: readonly Role[]
}

/**
 * Sidebar order. `to` is the URL prefix used for access checks.
 *
 * Dashboard leads because it is where `defaultPath` lands an admin, and a
 * rail whose first item is not the screen you were just dropped on reads as
 * misordered. A counter never sees it (admin-only), so a counter's rail still
 * opens on Till — the screen they land on — and the order holds for both.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, roles: ['admin'] },
  { id: 'pos', label: 'Till', to: '/pos', icon: ShoppingCart, roles: ['admin', 'counter'] },
  { id: 'day-close', label: 'Day close', to: '/day-close', icon: CalendarCheck, roles: ['admin', 'counter'] },
  { id: 'customers', label: 'Customers', to: '/customers', icon: BookUser, roles: ['admin', 'counter'] },
  { id: 'invoices', label: 'Invoices', to: '/invoices', icon: FileText, roles: ['admin', 'counter'] },
  { id: 'reports', label: 'Reports', to: '/reports', icon: BarChart3, roles: ['admin'] },
  { id: 'fbr', label: 'File with FBR', to: '/fbr', icon: FileCheck2, roles: ['admin'] },
  { id: 'rates', label: 'Rates', to: '/rates', icon: Scale, roles: ['admin'] },
  { id: 'settings', label: 'Settings', to: '/settings', icon: Settings, roles: ['admin'] },
]

/**
 * The rail for a role, minus the screens a store has switched off: Day close
 * goes when `day_close_enabled` is false (spec §6.7 addendum). Undefined
 * settings (still loading) hide nothing, so the rail never flickers an item
 * in after the fact. `canAccess` is untouched: the URL still opens, it is
 * only not offered.
 */
export function visibleNavItems(role: string, settings: { day_close_enabled: boolean } | undefined): NavItem[] {
  return NAV_ITEMS.filter((i) => i.roles.includes(role as Role) && !(i.id === 'day-close' && settings?.day_close_enabled === false))
}

/** Whether the role may open the URL (exact or prefix match on a nav item). */
export function canAccess(role: string, pathname: string): boolean {
  if (!isRole(role)) return false
  return NAV_ITEMS.some(
    (item) => item.roles.includes(role) && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
  )
}

/** First screen after login. */
export function defaultPath(role: string): '/dashboard' | '/pos' {
  return role === 'admin' ? '/dashboard' : '/pos'
}

export function roleLabel(role: string): string {
  switch (role) {
    case 'admin':
      return 'Admin'
    case 'counter':
      return 'Counter'
    default:
      return role
  }
}
